import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	Inject,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	TrackByFunction,
	ViewChild,
} from '@angular/core';
import { AsyncPipe, NgClass, NgFor, NgIf, NgStyle, isPlatformServer } from '@angular/common';
import {
	AppSettingsService,
	BreakPointService,
	ButtonComponent,
	CustomWindow,
	Device,
	SvgIconComponent,
	SwipeDirective,
	Movement,
	TrackImpressionDirective,
	WINDOW,
	PromoBannerImpressionArgs,
	FeatureService,
} from '@woolworthsnz/styleguide';
import { BannerCarouselApiModel, ContextResponse } from '@woolworthsnz/trader-api';
import { HeroItemComponent } from '../hero-item/hero-item.component';
import { HeroCarouselStore, CarouselRotationStatus } from './hero-carousel.store';
import { extractAnimationDuration, getCarouselStyle, isHorizontalMovement } from './hero-carousel.helpers';
import { first, Subject, takeUntil } from 'rxjs';
import { extractQLCodeFromCtaName } from 'libs/product/src/lib/helpers/product-response.helper';

const AUTO_ROTATION_INTERVAL_MS = 6000;

@Component({
	selector: 'wnz-hero-carousel',
	standalone: true,
	imports: [
		NgIf,
		NgFor,
		NgClass,
		AsyncPipe,
		NgStyle,
		ButtonComponent,
		HeroItemComponent,
		SvgIconComponent,
		SwipeDirective,
		TrackImpressionDirective,
	],
	providers: [HeroCarouselStore],
	templateUrl: './hero-carousel.component.html',
	styleUrls: ['./hero-carousel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroCarouselComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('heroCarousel') heroCarousel: ElementRef<HTMLElement>;
	@ViewChild(TrackImpressionDirective) trackImpressionDirective: TrackImpressionDirective;
	heroItems: BannerCarouselApiModel[] = [];
	rotationStatus: CarouselRotationStatus;
	carouselStyle = {};
	liveStatusMapping: Record<CarouselRotationStatus, string> = {
		[CarouselRotationStatus.STARTED]: 'off',
		[CarouselRotationStatus.PAUSED]: 'off',
		[CarouselRotationStatus.STOPPED]: 'polite',
	};

	public currentIndex = 0;
	public currentIndex$ = this.heroCarouselStore.selectCurrentIndex$;
	public heroItems$ = this.heroCarouselStore.selectHeroItems$;
	public timerId?: number;
	public toLeft$ = this.heroCarouselStore.selectToLeft$;
	public isCartologyGAMSessionGroup: boolean;
	promoBannerImpressionArgs: PromoBannerImpressionArgs[] = [];

	private rotationStatus$ = this.heroCarouselStore.selectRotationStatus$;

	private _toLeft: boolean;
	private _remainingTime = 0; // Track remaining time when paused
	private timerStartTime: number;
	private timing = `${this.appSettingsService.getTransition('medium')} ${this.appSettingsService.getEasing(
		'ease-in'
	)}`;
	private rotationStatusChangeHandlers: Record<
		CarouselRotationStatus,
		((previousRotationStatus: CarouselRotationStatus) => void) | (() => void)
	> = {
		[CarouselRotationStatus.STARTED]: (previousRotationStatus: CarouselRotationStatus): void => {
			if (previousRotationStatus === CarouselRotationStatus.STOPPED) {
				this.setAutoRotateTimeout(1);
			} else {
				this.setAutoRotateTimeout(this._remainingTime || AUTO_ROTATION_INTERVAL_MS);
				if (this._remainingTime) {
					this._remainingTime = 0;
				}
			}
		},
		[CarouselRotationStatus.PAUSED]: (): void => {
			this._remainingTime = this.calculateRemainingTime();
			this.cancelAutoRotation();
		},
		[CarouselRotationStatus.STOPPED]: (): void => {
			this.cancelAutoRotation();
		},
	};
	private destroyed$: Subject<boolean> = new Subject();
	private _isHomePageGA4BugfixEnable = false;
	private enableConsistencyAndPriorityForBanners = false;

	constructor(
		protected heroCarouselStore: HeroCarouselStore,
		private appSettingsService: AppSettingsService,
		public breakPointService: BreakPointService,
		private cdr: ChangeDetectorRef,
		private featureService: FeatureService,
		@Inject(WINDOW) private window: CustomWindow,
		@Inject(PLATFORM_ID) private platformId: object
	) {}

	get heroItemsCount(): number {
		return this.heroItems.length ?? 0;
	}

	@HostListener('window:resize')
	handleResize(): void {
		this.initCarousel();
	}

	private createDefaultBannerImpression(item: BannerCarouselApiModel, index: number): PromoBannerImpressionArgs {
		return {
			bannerPromotionId: item.id?.toString() ?? '',
			bannerPromotionName: item.name ?? '',
			creativeName360: 'Carousel',
			creativeSlot: (index + 1).toString() ?? '',
			carouselName: 'Container',
			imagePath: item.mainImageUrl ?? '',
			link: item.buttonHref ?? '',
			creativeName: 'Home page carousel banner',
		};
	}

	private createFeatureBannerImpression(
		item: BannerCarouselApiModel,
		index: number,
		enableConsistencyFlag = false
	): PromoBannerImpressionArgs {
		const originalName = item.name ?? '';
		const bannerPromotionId = extractQLCodeFromCtaName(originalName) ?? item.id?.toString() ?? '';
		const bannerPromotionName = originalName.includes('|') ? originalName.split('|')[0].trim() : originalName;
		const createSlotNumber =
			enableConsistencyFlag && item.additionalClassNames?.includes('cartology-gam-ad')
				? '2'
				: (index + 1).toString() ?? '';

		return {
			bannerPromotionId,
			bannerPromotionName: enableConsistencyFlag ? originalName : bannerPromotionName,
			creativeName360: 'Carousel',
			creativeSlot: createSlotNumber,
			carouselName: 'Container',
			imagePath: item.mainImageUrl ?? '',
			link: item.buttonHref ?? '',
			creativeName: 'Home page carousel banner',
		};
	}

	ngOnInit(): void {
		this._isHomePageGA4BugfixEnable = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.EnableGA4HomePageBugFix
		);

		this.enableConsistencyAndPriorityForBanners = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.EnableConsistencyAndPriorityForBanners
		);

		this.breakPointService.device$.pipe(takeUntil(this.destroyed$)).subscribe((device) => {
			this.timing = `${
				device === Device.MOBILE || device === Device.TABLETPORTRAIT
					? this.appSettingsService.getTransition('short')
					: this.appSettingsService.getTransition('medium')
			} ${this.appSettingsService.getEasing('ease-in')}`;
		});

		this.heroCarouselStore.loadHeroItems();
		this.heroCarouselStore.updateCurrentIndex(0);

		this.carouselStyle = getCarouselStyle(
			this.currentIndex,
			this.heroItems.length,
			this.timing,
			this._toLeft
		).staticStyle;

		this.toLeft$.pipe(takeUntil(this.destroyed$)).subscribe((toLeft: boolean) => {
			this._toLeft = toLeft;
		});

		this.currentIndex$.pipe(takeUntil(this.destroyed$)).subscribe((currentIndex) => {
			this.currentIndex = currentIndex;
		});

		this.isCartologyGAMSessionGroup = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.CartologyGAMSessionGroup
		);
		this.heroItems$.pipe(takeUntil(this.destroyed$)).subscribe((heroItems) => {
			this.heroItems = heroItems;
			if (this.heroItems?.length > 1) {
				this.initCarousel();
			}

			if (!this._isHomePageGA4BugfixEnable) {
				this.promoBannerImpressionArgs = heroItems
					.filter((item) => this.filterViewImpressions(item))
					.map(this.createDefaultBannerImpression);
			} else {
				if (this.enableConsistencyAndPriorityForBanners) {
					heroItems.forEach((item, index) => {
						if (this.filterViewImpressions(item)) {
							this.promoBannerImpressionArgs.push(
								this.createFeatureBannerImpression(
									item,
									index,
									this.enableConsistencyAndPriorityForBanners
								)
							);
						}
					});
				} else {
					this.promoBannerImpressionArgs = heroItems
						.filter((item) => this.filterViewImpressions(item))
						.map((item, index) =>
							this.createFeatureBannerImpression(item, index, this.enableConsistencyAndPriorityForBanners)
						);
				}
			}
		});

		this.rotationStatus$.pipe(takeUntil(this.destroyed$)).subscribe(this.handleRotationStatus.bind(this));
		if (this.isCartologyGAMSessionGroup) {
			window.addEventListener(
				'promo-element-ready-for-default-impression-observability',
				this.defaultImpressionEventHandler
			);
		}
	}

	ngAfterViewInit(): void {
		this.initCarousel();
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
		this.cancelAutoRotation();
		if (this.isCartologyGAMSessionGroup) {
			window.removeEventListener(
				'promo-element-ready-for-default-impression-observability',
				this.defaultImpressionEventHandler
			);
		}
	}

	onSwipeLeft(): void {
		this.nextClick();
	}

	onSwipeRight(): void {
		this.previousClick();
	}

	onSwipeMove(movement: Movement): void {
		const { x: horizontalMovement } = movement;
		if (isHorizontalMovement(movement) || horizontalMovement === 0) {
			this.carouselStyle = getCarouselStyle(
				this.currentIndex,
				this.heroItems.length,
				this.timing,
				this._toLeft,
				horizontalMovement
			).staticStyle;
		}
	}

	previousClick(): void {
		this.heroCarouselStore.updateToLeft(false);
		this.playPrevious();
		this.stopAutoRotation();
	}

	nextClick(): void {
		this.heroCarouselStore.updateToLeft(true);
		this.playNext();
		this.stopAutoRotation();
	}

	trackByHeroItemId: TrackByFunction<any> = (_, item: any) => item.id;

	startAutoRotation(): void {
		this.heroCarouselStore.updateRotationStatus(CarouselRotationStatus.STARTED);
	}

	stopAutoRotation(): void {
		if (this.rotationStatus === CarouselRotationStatus.STARTED) {
			this.heroCarouselStore.updateRotationStatus(CarouselRotationStatus.STOPPED);
		}
	}

	pauseAutoRotation(): void {
		if (this.rotationStatus === CarouselRotationStatus.STARTED) {
			this.heroCarouselStore.updateRotationStatus(CarouselRotationStatus.PAUSED);
		}
	}

	resumeAutoRotation(): void {
		if (this.rotationStatus === CarouselRotationStatus.PAUSED) {
			this.heroCarouselStore.updateRotationStatus(CarouselRotationStatus.STARTED);
		}
	}

	onTcLinkClick(): void {
		this.pauseAutoRotation();
	}

	private readonly defaultImpressionEventHandler = (event: Event): void => {
		const customEvent = event as CustomEvent;
		const eventElement = customEvent.detail?.element;
		if (!eventElement || eventElement?.id === 'cartology-homepage-carousel-2') {
			let promoBannerImpressionArgs: PromoBannerImpressionArgs[];
			if (!this._isHomePageGA4BugfixEnable) {
				promoBannerImpressionArgs = this.heroItems
					.filter((item) => !this.filterViewImpressions(item), first)
					.map(this.createDefaultBannerImpression);
			} else {
				promoBannerImpressionArgs = this.heroItems
					.filter((item) => !this.filterViewImpressions(item))
					.map((item, index) =>
						this.createFeatureBannerImpression(item, index, this.enableConsistencyAndPriorityForBanners)
					);
			}
			promoBannerImpressionArgs.forEach((promoBannerImpressionArg) => {
				this.trackImpressionDirective.cdxTrackImpression = promoBannerImpressionArg;
				this.trackImpressionDirective.initObserver();
			});
		}
	};

	private filterViewImpressions(item: BannerCarouselApiModel): boolean {
		return (
			!this.isCartologyGAMSessionGroup ||
			(this.isCartologyGAMSessionGroup && !item?.additionalClassNames?.includes('cartology-gam-ad'))
		);
	}

	private handleRotationStatus(rotationStatus: CarouselRotationStatus): void {
		const previousRotationStatus = this.rotationStatus;
		this.rotationStatus = rotationStatus;

		const handler = this.rotationStatusChangeHandlers[rotationStatus];

		if (handler) {
			handler.call(this, previousRotationStatus);
		}
	}

	private showCurrentSlide(): void {
		if (typeof this.currentIndex === 'undefined') {
			return;
		}

		this.doShowCurrentSlide(this._toLeft);
	}

	private doShowCurrentSlide(toLeft: boolean): void {
		const { animateStyle, staticStyle } = getCarouselStyle(
			this.currentIndex,
			this.heroItems.length,
			this.timing,
			toLeft
		);
		this.carouselStyle = animateStyle;

		const timeout = setTimeout(
			(): void => {
				clearTimeout(timeout);
				this.carouselStyle = staticStyle;
				this.cdr.markForCheck();
			},
			extractAnimationDuration(this.timing) || 0
		);
	}

	private initCarousel(): void {
		this.setAutoRotateTimeout();
	}

	private resetTimeout = (): void => {
		if (this.rotationStatus === CarouselRotationStatus.STARTED && this.timerId) {
			clearTimeout(this.timerId);
			this.setAutoRotateTimeout();
		}
	};

	private setAutoRotateTimeout(autoRotateInterval: number = AUTO_ROTATION_INTERVAL_MS): void {
		if (this.timerId) {
			clearTimeout(this.timerId);
		}

		if (
			this.rotationStatus === CarouselRotationStatus.STARTED &&
			autoRotateInterval &&
			!isPlatformServer(this.platformId)
		) {
			this.timerStartTime = Date.now();
			this.timerId = this.window.setTimeout(() => {
				this.heroCarouselStore.updateToLeft(true);
				this.playNext();
				this.resetTimeout();
			}, autoRotateInterval);
		}
	}

	private cancelAutoRotation(): void {
		if (this.timerId) {
			clearTimeout(this.timerId);
			this.timerId = undefined;
		}
	}

	private calculateRemainingTime(): number {
		const elapsedTime = Date.now() - this.timerStartTime;
		return Math.max(1, AUTO_ROTATION_INTERVAL_MS - elapsedTime);
	}

	private playPrevious = (): void => {
		if (!this.heroItems.length) {
			return;
		}
		this.heroCarouselStore.updateCurrentIndex(
			(this.currentIndex + this.heroItems.length - 1) % this.heroItems.length
		);
		this.cdr.markForCheck();
		this.showCurrentSlide();
	};

	private playNext = (): void => {
		if (!this.heroItems.length) {
			return;
		}
		this.heroCarouselStore.updateCurrentIndex((this.currentIndex + 1) % this.heroItems.length);
		this.cdr.markForCheck();
		this.showCurrentSlide();
	};

	public getAdditionalClassNames(bannerIndex: number, item: BannerCarouselApiModel): string {
		return `${this.currentIndex === bannerIndex ? 'active' : ''} ${this.isCartologyGAMSessionGroup && item?.additionalClassNames ? item.additionalClassNames : ''}`;
	}
}
